import React, { useContext, useEffect, useState } from 'react';
import useIntl from './use-intl.js';
import { LoadingIndicator, LoadingSkeleton } from '#lib/components/loading.jsx';
import IntlTree from './intlTree.jsx';
import Input from '#lib/components/input.jsx';
import Button from '#lib/components/button.jsx';
import TextArea from '#lib/components/textarea.jsx';
import { addClasses } from '#lib/helpers/class.js';
import { Icon } from '@iconify-icon/react';
import intlHooks from './intlHooks.js';
import ManageControls from '#components/manage-controls.jsx';
import { UploadPanel } from './upload-panel.jsx';
import useIntlCsv from './use-intl-csv.js';
import { UserContext } from '#src/index.jsx';
import { capitalize } from '#lib/utils/string.js';

// INTL editor
export default function IntlEditor() {
    const { data, flags, save, saveAll } = useIntl();
    const [isUpdatingText, setIsUpdatingText] = useState(false);
    const [isDownloadingCSVFile, setIsDownloadingCSVFile] = useState(false);
    const { csv, download, upload } = useIntlCsv({ setIsUpdatingText, setIsDownloadingCSVFile });
    const { getLanguage } = useContext(UserContext);
    const userLanguage = getLanguage();

    const { makeNewTree, deepValue, setDeepValue } = intlHooks();
    const [content, setContent] = useState(null);
    const [show, setShow] = useState(false);
    const [showUploadPanel, setShowUploadPanel] = useState(false);
    const [filter, setFilter] = useState('');
    const [tree, setTree] = useState([]);

    const onClick = (value) => {
        setContent(() => {
            const result = { path: value };
            for (const lang of INTL_LANGS.split(',')) {
                result[lang] = deepValue(data.files[lang], value);
            }
            return result;
        });
        setShow(true);
    };

    const onChange = (e) => setFilter(() => e.target.value);

    const onChangeInput = (e, lang) => {
        const temp = data.files[lang];
        setDeepValue(temp, content.path, e.target.value);
        const updated = structuredClone(content);
        updated[lang] = e.target.value;
        setContent(() => updated);
    };

    const handleDownloadCSVFile = () => {
        const confirm = window.confirm(_t.intl.downloadConfirm());
        if (!confirm) return;
        setIsDownloadingCSVFile(true);
        download();
    };

    const handleUploadCSVFile = () => {
        setShowUploadPanel(true);
    };

    const onSubmitFilter = () => {
        // NO FILTER
        if (!filter) return setTree(data.files[INTL_LANGS.split(',')[0]]);

        //PATH FILTER (global.username)
        if (filter.split('.').length > 1) {
            const newTree = {};
            makeNewTree(newTree, data.files[INTL_LANGS.split(',')[0]], filter);

            return setTree(newTree);
        }

        //NORMAL FILTER (property name / query on the text)
        const { it: itaTexts, en: engTexts } = structuredClone(data.files);
        let multipleTrees = filterTree(engTexts, filter);
        multipleTrees = Object.assign(multipleTrees, filterTree(itaTexts, filter));

        return setTree(multipleTrees);
    };

    const filterTree = (obj, filter) => {
        let result = Array.isArray(obj) ? [] : {}; // Crea un array se obj è un array, altrimenti un oggetto

        for (let key in obj) {
            if (obj.hasOwnProperty(key)) {
                let value = obj[key];

                if (
                    typeof value === 'string' &&
                    (value.toLowerCase().includes(filter.toLowerCase()) ||
                        key.toLowerCase().includes(filter.toLowerCase()))
                ) {
                    result[key] = value;
                    continue;
                }

                // Se il value è un oggetto o un array, filtra ricorsivamente
                if (typeof value === 'object' && value !== null) {
                    let filtered = filterTree(value, filter); // Ricorsione

                    // Aggiungi il sottoalbero filtrato solo se contiene risultati validi
                    if (Object.keys(filtered).length > 0) {
                        result[key] = filtered;
                    }
                }
            }
        }

        return result;
    };

    useEffect(() => {
        if (!data) return;
        setTree(data.files?.[INTL_LANGS.split(',')[0]]);
    }, [data]);

    useEffect(() => {
        if (csv) {
            const csvData = new Blob([csv], { type: 'text/csv' });
            const csvURL = URL.createObjectURL(csvData);
            const link = document.createElement('a');
            link.href = csvURL;
            link.download = `intl.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(csvURL);
            setIsDownloadingCSVFile(false);
        }
    }, [csv]);

    if (flags.isLoading || tree?.length === 0)
        return (
            <LoadingSkeleton>
                <LoadingIndicator />
            </LoadingSkeleton>
        );

    const editorHeight = window.innerHeight - 116;

    return (
        <div className="flex flex-col min-h-screen">
            <div className="fixed w-full h-[116px] bg-white top-0 left-0 z-50 shadow-sm">
                <ManageControls.BarSlot>
                    <p className="text-2xl">{_t.intl.title()}</p>
                </ManageControls.BarSlot>
                <ManageControls.ButtonSlot>
                    <Button size="xs" look="white" busy={isDownloadingCSVFile} onClick={handleDownloadCSVFile}>
                        {_t.intl.download()}
                    </Button>
                    <Button size="xs" look="white" busy={isUpdatingText} onClick={handleUploadCSVFile}>
                        {_t.intl.upload()}
                    </Button>
                    <Button size="xs" look="white" onClick={() => saveAll.mutate(data.files)} busy={saveAll.isLoading}>
                        {_t.intl.saveAll()}
                    </Button>
                </ManageControls.ButtonSlot>
            </div>
            <div className="flex flex-col md:flex-row pt-[116px]">
                <div
                    className="w-full md:w-1/3 md:px-4 md:pt-2 bg-neutral-100 gap-4 flex flex-col overflow-auto"
                    style={{ height: editorHeight }}
                >
                    <span className="text-justify hyphens-auto text-sm text-neutral-800" lang="en">
                        {_t.intl.description()}
                    </span>
                    <div className="flex gap-2 items-end pb-2">
                        <Input.Full
                            className="w-full"
                            onChange={onChange}
                            type="text"
                            name="name"
                            placeholder={_t.intl.filterLabel()}
                        />
                        <Button
                            look="primary"
                            onClick={onSubmitFilter}
                            className={addClasses('h-full w-1/3 flex justify-center items-center')}
                        >
                            <Icon icon="material-symbols:search-rounded" width="1.3rem" height="1.3rem" />
                        </Button>
                    </div>
                    <div className="w-full h-full overflow-auto bg-neutral-50 border border-neutral-200 rounded-lg">
                        <IntlTree tree={tree} onClick={onClick} content={content} />
                    </div>
                </div>
                <div
                    className="w-full md:w-[70%] flex flex-col gap-4 overflow-y-auto py-4 md:py-2 md:px-4"
                    style={{ height: editorHeight }}
                >
                    <div className="flex flex-col gap-2">
                        {INTL_LANGS.split(',').map((lang) => {
                            const displayLang = new Intl.DisplayNames([userLanguage ?? 'en'], {
                                type: 'language'
                            }).of(lang);
                            return (
                                <TextArea.Full
                                    disabled={!show}
                                    key={lang}
                                    id={lang}
                                    type="text"
                                    label={capitalize(displayLang)}
                                    name={lang}
                                    value={content?.[lang] ?? ''}
                                    onInput={(value) => onChangeInput(value, lang)}
                                    rows="5"
                                />
                            );
                        })}
                    </div>
                    <Button
                        className={addClasses('w-[10%]')}
                        look="primary"
                        onClick={() => save.mutate(content)}
                        disabled={!show}
                    >
                        {_t.global.save()}
                    </Button>
                </div>
                {showUploadPanel && (
                    <UploadPanel
                        isOpen={showUploadPanel}
                        onClose={() => setShowUploadPanel(false)}
                        upload={upload}
                        setIsUpdatingText={setIsUpdatingText}
                    />
                )}
            </div>
        </div>
    );
}
